import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { requiredOptionSchema } from '~/features/types';

/** [MCV-1147] Strict YYYY-MM-DD validator. Without this, inputs like
 *  "12/12/54545" passed Zod (since issueDate/expiryDate were just
 *  `z.string().optional()`) and either fell through to the refine with a
 *  misleading "Issue Date must be before Expired Date" message, or hit the
 *  backend and produced a vague corner toast. */
const isStrictIsoDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const optionalIsoDate = z
  .string()
  .nullable()
  .optional()
  .refine((val) => !val || isStrictIsoDate(val), {
    message: 'Must be a valid date (YYYY-MM-DD)',
  });

// [MCV-1721] Type of Certification and Issuer carry the required asterisk in
// CertForm, so both must be enforced here (MCV-1718: a marker never claims more
// than validation enforces). Legacy rows are not frozen: transformCertToFormValue
// coerces blank values to the first option at the edit boundary.
export const certSchema = z
  .object({
    activeFlag: z.boolean(),
    certName: requiredOptionSchema('Type of Certification is required'),
    certNum: z
      .string()
      .max(64, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64) })
      .optional(),
    expiryDate: optionalIsoDate,
    id: z.number().optional(),
    issueDate: optionalIsoDate,
    issuer: requiredOptionSchema('Issuer is required'),
    validFlag: z.boolean().optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (data) =>
      !data.expiryDate || !data.issueDate || new Date(data.issueDate) < new Date(data.expiryDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Issue Date', 'Expired Date'),
      path: ['issueDate'],
    }
  );
